/**
 * @file src/lib/routerErrorHandling.tsx
 * @exports routerErrorHandling
 * @summary Router-level error handling: every route renders AppErrorFallback when it throws, and the
 *          error is reported to Sentry from there.
 * @remarks TanStack wraps each route match in a CatchBoundary only when an error component is configured.
 *          Before FRN-3612 none was, so a route whose chunk failed to load fell through to Sentry's boundary
 *          in App, which has no fallback and renders nothing: the blank page. That boundary sits above the
 *          router, so errors caught here never reach it; `defaultOnCatch` reports them instead.
 */

import * as Sentry from "@sentry/react";

import { AppErrorFallback, isModuleLoadError } from "src/components/AppErrorFallback/AppErrorFallback";

import pkg from "../../package.json";

/** Sends a route error to Sentry, tagged so missing-chunk failures can be counted per deploy. */
const reportRouteError = (error: Error): void => {
	Sentry.captureException(error, {
		tags: {
			app_version: pkg.version,
			caught_by: "route",
			module_load_error: isModuleLoadError(error) ? "yes" : "no",
		},
	});
};

/** Spread into `createRouter`: the fallback every route renders on error, and the Sentry report for it. */
export const routerErrorHandling = {
	defaultErrorComponent: AppErrorFallback,
	defaultOnCatch: reportRouteError,
} as const;
